
import { useState } from 'react';
import { supabase } from '../../utils/supabase';

export default function Login() {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);


  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(''); // Limpiar error al escribir
  };


  // Function to check if user has a valid plan
  const checkUserPlanValidity = (userData: any) => {
    // Check if email is confirmed
    if (!userData.email_confirm) {
      return false;
    }

    // Allow users without plans to login - they'll see subscription options
    // Only block users if they have an invalid plan status
    if (userData.plan_status === 'cancelled' || userData.plan_status === 'expired') {
      return false;
    }

    // Allow users with no plan, trial, or active plans to login
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // 1. Autenticar directamente con email y contraseña usando Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password
      });

      if (authError || !authData.user) {
        setError('Email o contraseña incorrectos');
        setIsLoading(false);
        return;
      }

      // 2. Buscar los datos del usuario por email
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('email, username, shelter_name, city, state, phone, plan, stripe_customer_id, email_confirm')
        .eq('email', formData.email)
        .single();

      if (userError || !userData) {
        setError('Error al cargar datos del usuario');
        setIsLoading(false);
        return;
      }

      // 3. Verificar si el usuario tiene un plan válido
      const hasValidPlan = checkUserPlanValidity(userData);
      if (!hasValidPlan) {
        if (!userData.email_confirm) {
          setError('Tu email no ha sido confirmado. Por favor revisa tu bandeja de entrada y haz clic en el enlace de confirmación.');
        } else {
          setError('Tu cuenta no está activa. Por favor contacta al soporte.');
        }
        setIsLoading(false);
        return;
      }

      // 4. Crear datos de sesión con timestamp de expiración (3 horas)
      const sessionData = {
        id: authData.user.id,
        ...userData,
        loginTime: Date.now(),
        expiresAt: Date.now() + (3 * 60 * 60 * 1000) // 3 horas en milisegundos
      };

      // Guardar datos del usuario en localStorage con expiración
      localStorage.setItem('user_session', JSON.stringify(sessionData));
      
      // Verificar si es administrador maestro
      if (userData.plan === 'master') {
        // Redirigir al panel de administración maestro
        window.REACT_APP_NAVIGATE('/master-admin');
      } else {
        // Redirigir al panel de administración normal
        window.REACT_APP_NAVIGATE('/admin');
      }
      
    } catch (error) {
      console.error('Error al iniciar sesión:', error);
      setError('Error al procesar el inicio de sesión');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-blue-50">
      {/* Header */}
      <header className="bg-white shadow-lg">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-4">
            <div 
              className="flex items-center space-x-3 cursor-pointer"
              onClick={() => window.REACT_APP_NAVIGATE('/')}
            >
              <img 
                src="/images/image.png" 
                alt="Huellitas Unidas Logo" 
                className="w-12 h-12 object-contain"
              />
              <div>
                <h1 className="text-xl font-bold text-gray-800" style={{ fontFamily: '"Pacifico", serif' }}>
                  Huellitas Unidas
                </h1>
                <p className="text-xs text-gray-600">Salvando vidas, una patita a la vez</p>
              </div>
            </div>
            
            <button 
              onClick={() => window.REACT_APP_NAVIGATE('/')}
              className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              <span>Volver al inicio</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex items-center justify-center min-h-[calc(100vh-80px)] py-12">
        <div className="w-full max-w-md">
          {/* Login Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-admin-fill text-2xl text-orange-500"></i>
              </div>
              <h2 className="text-3xl font-bold text-gray-800 mb-2">Panel Administrador</h2>
              <p className="text-gray-600">Ingresa a tu cuenta de refugio</p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex items-center">
                  <i className="ri-error-warning-fill text-red-500 mr-2"></i>
                  <span className="text-red-700 text-sm">{error}</span>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <div className="relative">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="tu@email.com"
                    required
                  />
                  <i className="ri-mail-line text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2"></i>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contraseña
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <i className="ri-lock-line text-gray-400"></i>
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Ingresa tu contraseña"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                  </button>
                </div>
              </div>

              <div className="text-right">
                <button
                  type="button"
                  onClick={() => window.REACT_APP_NAVIGATE('/forgot-password')}
                  className="text-orange-500 hover:text-orange-600 text-sm font-medium transition-colors cursor-pointer"
                >
                  ¿Olvidaste tu contraseña?
                </button>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 rounded-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isLoading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Iniciando sesión...
                  </>
                ) : (
                  <>
                    <i className="ri-login-box-line mr-2"></i>
                    Iniciar Sesión
                  </>
                )}
              </button>
            </form>

            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-4">¿No tienes cuenta?</p>
                <button
                  onClick={() => window.REACT_APP_NAVIGATE('/register')}
                  className="text-orange-500 hover:text-orange-600 font-medium transition-colors cursor-pointer"
                >
                  Registrar nuevo refugio
                </button>
              </div>
            </div>
          </div>
          
          {/* Additional Info */}
          <div className="mt-8 text-center">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl mx-auto">
              <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4">
                <i className="ri-shield-check-line text-2xl text-green-500 mb-2"></i>
                <h3 className="text-sm font-semibold text-gray-800">Seguro</h3>
                <p className="text-xs text-gray-600">Tus datos están protegidos</p>
              </div>
              <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4">
                <i className="ri-time-line text-2xl text-blue-500 mb-2"></i>
                <h3 className="text-sm font-semibold text-gray-800">Sesión 3h</h3>
                <p className="text-xs text-gray-600">Mantén tu sesión activa</p>
              </div>
              <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4">
                <i className="ri-customer-service-2-line text-2xl text-purple-500 mb-2"></i>
                <h3 className="text-sm font-semibold text-gray-800">Soporte</h3>
                <p className="text-xs text-gray-600">Ayuda cuando la necesites</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
